import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'

type DatadogActionContext = Record<string, string | number | boolean | null | undefined>

interface DatadogUserContext {
  id: string
  name?: string
  email?: string
  [key: string]: unknown
}

let isRumInitialized = false

function toBooleanFlag(value: string | undefined): boolean {
  return value === '1' || value === 'true'
}

function toNumber(value: string | undefined, fallback: number): number {
  const parsedValue = Number(value)
  return Number.isFinite(parsedValue) ? parsedValue : fallback
}

export function initDatadog(): void {
  const isDatadogEnabled = toBooleanFlag(import.meta.env.VITE_DATADOG_ENABLED)

  if (!isDatadogEnabled) {
    return
  }

  const {
    VITE_DATADOG_APPLICATION_ID,
    VITE_DATADOG_CLIENT_TOKEN,
    VITE_DATADOG_SITE,
    VITE_DATADOG_SERVICE,
    VITE_DATADOG_ENV
  } = import.meta.env

  if (!VITE_DATADOG_APPLICATION_ID || !VITE_DATADOG_CLIENT_TOKEN) {
    console.warn('Datadog is enabled but application id or client token is missing.')
    return
  }

  const sampleRate = toNumber(import.meta.env.VITE_DATADOG_SESSION_SAMPLE_RATE, 100)
  const sessionReplaySampleRate = toNumber(import.meta.env.VITE_DATADOG_SESSION_REPLAY_SAMPLE_RATE, 100)

  const sharedConfig = {
    clientToken: VITE_DATADOG_CLIENT_TOKEN,
    env: VITE_DATADOG_ENV || (import.meta.env.PROD ? 'production' : 'development'),
    service: VITE_DATADOG_SERVICE || 'vack',
    site: VITE_DATADOG_SITE || 'datadoghq.com',
    sessionSampleRate: sampleRate
  }

  datadogRum.init({
    ...sharedConfig,
    applicationId: VITE_DATADOG_APPLICATION_ID,
    sessionReplaySampleRate,
    trackUserInteractions: true,
    trackBfcacheViews: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  })

  datadogRum.startSessionReplayRecording()

  isRumInitialized = true

  datadogLogs.init({
    ...sharedConfig,
    forwardErrorsToLogs: true
  })
}

export function trackDatadogAction(actionName: string, context?: DatadogActionContext): void {
  if (!isRumInitialized) {
    return
  }

  datadogRum.addAction(actionName, context)
}

export function setDatadogUser(user: DatadogUserContext): void {
  if (!isRumInitialized) {
    return
  }

  datadogRum.setUser(user)
}

export function setDatadogGlobalContextProperty(key: string, value: string) {
  if (!isRumInitialized) {
    return
  }

  datadogRum.setGlobalContextProperty(key, value)
}